// src/contexts/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Profile } from '../types/database';
import { profilesApi } from '../api/profiles';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Add a timeout for session fetching
    const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T> => {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Operation timed out')), ms)
      );
      return Promise.race([promise, timeout]);
    };

    const initializeAuth = async () => {
      try {
        console.log('[AuthProvider] Initializing auth...');
        const { data, error } = await withTimeout(supabase.auth.getSession(), 5000); // 5-second timeout
        if (!mounted) return;

        if (error) {
          console.error('[AuthProvider] Error getting session:', error.message);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        const currentUser = data?.session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          try {
            console.log('[AuthProvider] Fetching profile for user:', currentUser.id);
            const userProfile = await profilesApi.getProfile(currentUser.id);
            console.log('[AuthProvider] Profile fetched:', userProfile);
            setProfile(userProfile);
          } catch (profileError) {
            console.error('[AuthProvider] Error loading user profile:', profileError);
            setProfile(null);
          }
        }
      } catch (err) {
        console.error('[AuthProvider] Unexpected auth error:', err);
        setUser(null);
        setProfile(null);
      } finally {
        if (mounted) {
          setLoading(false);
          console.log('[AuthProvider] Loading complete, user:', user);
        }
      }
    };

    initializeAuth();

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('[AuthProvider] Auth state changed:', _event, 'Session:', session);
      if (!mounted) return;

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        try {
          console.log('[AuthProvider] Fetching profile on auth change:', currentUser.id);
          const userProfile = await profilesApi.getProfile(currentUser.id);
          console.log('[AuthProvider] Profile fetched on auth change:', userProfile);
          setProfile(userProfile);
        } catch (error) {
          console.error('[AuthProvider] Error fetching profile on auth change:', error);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
      console.log('[AuthProvider] Auth state change complete, user:', currentUser);
    });

    return () => {
      mounted = false;
      subscription?.subscription?.unsubscribe?.();
      console.log('[AuthProvider] Cleanup: Unsubscribed from auth state changes');
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      console.log('[AuthProvider] Initiating Google Sign-In');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) {
        console.error('[AuthProvider] Google Sign-In error:', error.message);
        throw new Error(error.message || 'Failed to initiate Google Sign-In');
      }
    } catch (error) {
      console.error('[AuthProvider] Google Sign-In failed:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('[AuthProvider] Signing out');
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      console.log('[AuthProvider] Sign out successful');
    } catch (error) {
      console.error('[AuthProvider] Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
